import type { GetOrdersRequest } from '@/core/generated/api';
import { orderListService } from '../api/orderListService';
import { orderService } from '../api/orderService';
import { orderTableFiltersToListRequest } from '../mappers';
import { FilterMatchMode } from 'primereact/api';
import type { OrderState } from './types';
import { create } from 'zustand';
import { getErrorMessage } from '@/shared/lib/errors';
import { RequestStatus } from '@/shared/model/requestStatus';
import { OrderSortField } from '@/core/generated/api';

let latestOrderRequest = 0;
let latestListRequest = 0;
let latestClientOrdersRequest = 0;
let latestExecutorOrdersRequest = 0;

export const ORDER_INITIAL_FILTERS = {
    created: {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },
    startOrderTime: {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },
    formedAt: {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },
    completedAt: {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },

    orderNumber: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    clientName: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    petName: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    executorFio: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    clientPhone: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    executorPhone: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    isPaymentSentToExecutor: {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },
    isExecutorSignedAcceptanceAct: {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },
    manualFnsReceipt: {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },
    hasReview: {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },
    'orderStatus.id': {
        value: null,
        matchMode: FilterMatchMode.IN
    },
    'orderType.id': {
        value: null,
        matchMode: FilterMatchMode.IN
    },
    'address.cityId': {
        value: null,
        matchMode: FilterMatchMode.EQUALS
    },
};

/** Хранит данные списка и выбранного заказа */
export const useOrders = create<OrderState>()((set) => ({
    order: null,
    detailsStatus: RequestStatus.Idle,
    detailsError: null,
    orders: [],
    listStatus: RequestStatus.Idle,
    listError: null,
    clientOrders: [],
    clientOrdersStatus: RequestStatus.Idle,
    clientOrdersError: null,
    executorOrders: [],
    executorOrdersStatus: RequestStatus.Idle,
    executorOrdersError: null,
    allCount: 0,
    allClientOrderCount: 0,
    allExecutorOrderCount: 0,

    filters: ORDER_INITIAL_FILTERS,

    setFilters: (filters) => set({ filters }),

    fetchOrders: async (params) => {
        const requestId = ++latestListRequest;

        set({
            listStatus: RequestStatus.Loading,
            listError: null
        });

        try {
            const response = await orderListService.getList(orderTableFiltersToListRequest(params));

            if (requestId !== latestListRequest) return;

            set({
                orders: response.orders ?? [],
                allCount: response.allCount ?? 0,
                listStatus: RequestStatus.Success,
            });
        } catch (error: unknown) {
            if (requestId !== latestListRequest) return;

            set({
                orders: [],
                allCount: 0,
                listStatus: RequestStatus.Error,
                listError: getErrorMessage(error, 'Не удалось загрузить заказы'),
            });
        }
    },

    getOrdersByClientId: async (
        id: string,
        take: number,
        skip: number,
        filters: GetOrdersRequest = {}
    ) => {
        const requestId = ++latestClientOrdersRequest;

        set({
            clientOrders: [],
            allClientOrderCount: 0,
            clientOrdersStatus: RequestStatus.Loading,
            clientOrdersError: null
        });

        try {
            const response = await orderListService.getList({
                ...filters,
                clientId: id,
                takeCount: take,
                skipCount: skip,
                sortBy: filters.sortBy ?? OrderSortField.FormedAt,
                ascending: false,
            });

            if (requestId !== latestClientOrdersRequest) return;

            set({
                clientOrders: response.orders ?? [],
                allClientOrderCount: response.allCount ?? 0,
                clientOrdersStatus: RequestStatus.Success,
            });
        } catch (error: unknown) {
            if (requestId !== latestClientOrdersRequest) return;

            set({
                clientOrders: [],
                allClientOrderCount: 0,
                clientOrdersStatus: RequestStatus.Error,
                clientOrdersError: getErrorMessage(error, 'Не удалось загрузить заказы клиента'),
            });
        }
    },

    getOrdersByExecutorId: async (
        id: string,
        take: number,
        skip: number,
        filters: GetOrdersRequest = {}
    ) => {
        const requestId = ++latestExecutorOrdersRequest;

        set({
            executorOrders: [],
            allExecutorOrderCount: 0,
            executorOrdersStatus: RequestStatus.Loading,
            executorOrdersError: null
        });

        try {
            const response = await orderListService.getList({
                ...filters,
                executorId: id,
                takeCount: take,
                skipCount: skip
            });

            if (requestId !== latestExecutorOrdersRequest) return;

            set({
                executorOrders: response.orders ?? [],
                allExecutorOrderCount: response.allCount ?? 0,
                executorOrdersStatus: RequestStatus.Success,
            });
        } catch (error: unknown) {
            if (requestId !== latestExecutorOrdersRequest) return;

            set({
                executorOrders: [],
                allExecutorOrderCount: 0,
                executorOrdersStatus: RequestStatus.Error,
                executorOrdersError: getErrorMessage(error, 'Не удалось загрузить заказы исполнителя'),
            });
        }
    },

    getOrderById: async (id: string) => {
        const requestId = ++latestOrderRequest;

        set({
            order: null,
            detailsStatus: RequestStatus.Loading,
            detailsError: null
        });

        try {
            const order = await orderService.getOrderById(id);

            if (requestId !== latestOrderRequest) return;

            set({
                order,
                detailsStatus: RequestStatus.Success,
                detailsError: null,
            });
        } catch (error) {
            if (requestId !== latestOrderRequest) return;

            set({
                order: null,
                detailsStatus: RequestStatus.Error,
                detailsError: getErrorMessage(error, 'Не удалось загрузить заказ'),
            });
        }
    },

    clearOrderState: () => {
        latestOrderRequest += 1;

        set({
            order: null,
            detailsStatus: RequestStatus.Idle,
            detailsError: null
        });
    },

    clearOrdersState: () => {
        latestListRequest += 1;

        set({
            orders: [],
            allCount: 0,
            listStatus: RequestStatus.Idle,
            listError: null
        });
    },

    clearClientOrders: () => {
        latestClientOrdersRequest += 1;

        set({
            clientOrders: [],
            allClientOrderCount: 0,
            clientOrdersStatus: RequestStatus.Idle,
            clientOrdersError: null
        });
    },

    clearExecutorOrders: () => {
        latestExecutorOrdersRequest += 1;

        set({
            executorOrders: [],
            allExecutorOrderCount: 0,
            executorOrdersStatus: RequestStatus.Idle,
            executorOrdersError: null
        });
    },
}));
